import { useForm, zodResolver } from '@/common'
import { reportError } from '@/components/errors'
import { clerkErrorOverrides, errorToString } from '@/lib/errors'
import type { Route } from 'next'
import { Routes } from '@/lib/routes'
import { safeRedirectUrl } from '@/lib/utils'
import { useSignIn } from '@clerk/nextjs'
import { useSearchParams } from 'next/navigation'
import { FC, useEffect, useState } from 'react'
import { z } from 'zod'
import { type MFAState } from './logic'
import { SignInFormView } from './sign-in-form-view'
import { useCompleteSignIn } from './use-complete-sign-in'

const signInSchema = z.object({
    email: z.string().min(1, 'Email is required').max(250, 'Email too long').email('Invalid email'),
    password: z.string().min(1, 'Required'),
})

type SignInFormData = z.infer<typeof signInSchema>

// Clerk's session_exists longMessage, thrown by signIn.create when a session is live.
const ALREADY_SIGNED_IN_MESSAGE = "You're already signed in."

export const SignInForm: FC<{
    mfa: MFAState
    onComplete: (state: MFAState) => Promise<void>
}> = ({ mfa, onComplete }) => {
    const { setActive, signIn } = useSignIn()
    const completeSignIn = useCompleteSignIn()
    const searchParams = useSearchParams()
    const [clerkError, setClerkError] = useState<{ title: string; message: string } | null>(null)

    useEffect(() => {
        if (searchParams.get('invite_not_found')) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setClerkError({
                title: 'Invite not found',
                message: 'The invitation link you followed is invalid or has already been used.',
            })
        }
        if (searchParams.get('error') === 'session') {
            setClerkError({
                title: 'Session Error',
                message: 'There was a problem with your session. Please sign in again.',
            })
        }
    }, [searchParams])

    const form = useForm<SignInFormData>({
        initialValues: {
            email: '',
            password: '',
        },
        // First-issue priority: the resolver keeps the LAST issue by default, so a blank email
        // failing both `min(1)` and `email()` reported "Invalid email" (OTTER-647).
        validate: zodResolver(signInSchema, { errorPriority: 'first' }),
    })

    if (!signIn || mfa) return null

    // Default landing is the dashboard (OTTER-671).
    const rawRedirect = searchParams.get('redirect_url')
    const validatedRedirect = safeRedirectUrl(rawRedirect, Routes.dashboard)
    const forgotPasswordHref = (
        rawRedirect && validatedRedirect !== Routes.dashboard
            ? `${Routes.accountResetPassword}?redirect_url=${encodeURIComponent(validatedRedirect)}`
            : Routes.accountResetPassword
    ) as Route

    const onSubmit = form.onSubmit(async (values) => {
        try {
            const attempt = await signIn.create({
                identifier: values.email,
                password: values.password,
            })
            if (attempt.status === 'complete') {
                await setActive({ session: attempt.createdSessionId })
                await onComplete(false)
                await completeSignIn()
            }
            if (attempt.status === 'needs_second_factor') {
                await onComplete({ signIn: attempt, usingSMS: false })
            }
        } catch (err: unknown) {
            const errorMessage = errorToString(err, { clerkOverrides: clerkErrorOverrides })

            // A session was restored in another tab between mount and submit. A full load, because
            // the root layout rendered for a signed-out visitor and carries no org list.
            if (errorMessage === ALREADY_SIGNED_IN_MESSAGE) {
                window.location.replace(validatedRedirect)
                return
            }

            if (
                errorMessage === clerkErrorOverrides.form_password_incorrect ||
                errorMessage === clerkErrorOverrides.form_identifier_not_found
            ) {
                form.setFieldError('email', ' ')
                form.setFieldError('password', errorMessage)
                return
            }

            // Only failures the branches above did not already answer. Reporting up front put a red
            // toast in front of the redirect and of a mistyped password (OTTER-745).
            reportError(err, 'Failed Signin Attempt')

            let title = 'Sign-in Error'
            if (err && typeof err === 'object' && 'errors' in err && Array.isArray(err.errors)) {
                const lockedError = err.errors.find((e: { code?: string }) => e.code === 'user_locked')
                if (lockedError) {
                    title = 'Account Locked'
                }
            }

            setClerkError({
                title,
                message: errorMessage || 'An error occurred during sign-in. Please try again.',
            })
        }
    })

    return (
        <SignInFormView
            form={form}
            onSubmit={onSubmit}
            forgotPasswordHref={forgotPasswordHref}
            clerkError={clerkError}
            setClerkError={setClerkError}
        />
    )
}
